import React, { useCallback, useEffect, useState } from "react";
import { Pagination } from "@mui/material";
import { useToast } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";

import Sidebar from "../../../components/Sidebar";
import Navbar from "../../../components/Navbar";

import {
  ApiUrlProvide,
  decryptURL,
  encryptURL,
  headerProvider,
  showToastMessage,
} from "../../../common/CommonFunctions";

import { getAccountDeletionUsersList } from "../../../services/authApi/authProvider";
import { searchByEmailId } from "../../../services/dataApi/dataProvider";

const AccountDeletion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const searchParams = new URLSearchParams(location.search);

  // Derive parameters from URL
  const pageNumber = Number(searchParams.get("page")) || 1;
  const sortingField =
    decryptURL(searchParams.get("sortField")) || "deletionRequestedAt";
  const sortingOrder = decryptURL(searchParams.get("sortOrder")) || "DESC";
  const filter = decryptURL(searchParams.get("filter")) || "all";

  // Decrypt userIdentifier (which holds the searched email/identifier)
  const userIdentifierFromURL = decryptURL(searchParams.get("userIdentifier")) || "";

  const [loader, setLoader] = useState(false);
  const [carUsersList, setCarUserList] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const itemsPerPage = 10;

  const extractUsersArray = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.response)) return res.response;
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.users)) return res.users;
    if (res.response && Array.isArray(res.response.users)) return res.response.users;
    return [];
  };

  // 1. Fetch by userIdentifier (Email search)
  const fetchUsersByIdentifier = useCallback(
    async (identifier) => {
      try {
        setLoader(true);
        const headers = headerProvider();
        const requestOptions = {
          method: "POST",
          headers,
          body: JSON.stringify({ email: identifier }),
        };

        const response = await searchByEmailId(requestOptions, identifier);
        const records = extractUsersArray(response);

        if (records.length > 0) {
          setCarUserList(records);
          const total = response?.totalCount || records.length;
          setPageCount(Math.ceil(total / itemsPerPage));
        } else {
          setCarUserList([]);
          setPageCount(0);
          showToastMessage("Error", "No user found for this identifier.", "error", toast);
        }
      } catch (err) {
        console.error("Search failed:", err);
        showToastMessage("Error", "Failed to search user.", "error", toast);
      } finally {
        setLoader(false);
      }
    },
    [itemsPerPage, toast]
  );

  // 2. Fetch default paginated list
  const fetchDefaultList = useCallback(async () => {
    try {
      setLoader(true);
      const queryParams = ApiUrlProvide(
        filter,
        pageNumber,
        itemsPerPage,
        sortingField,
        sortingOrder
      );
      const headers = headerProvider();
      const response = await getAccountDeletionUsersList(queryParams, headers);

      const records = extractUsersArray(response);
      setCarUserList(records);
      const total = response?.totalCount || records.length;
      setPageCount(Math.ceil(total / itemsPerPage));
    } catch (error) {
      console.error("Fetch list error:", error);
      showToastMessage("Error", "Something went wrong. Please try again.", "error", toast);
    } finally {
      setLoader(false);
    }
  }, [filter, pageNumber, itemsPerPage, sortingField, sortingOrder, toast]);

  // 3. Controller: triggers fetch based on decrypted URL value
  useEffect(() => {
    if (userIdentifierFromURL) {
      fetchUsersByIdentifier(userIdentifierFromURL);
    } else {
      fetchDefaultList();
    }
  }, [userIdentifierFromURL, fetchUsersByIdentifier, fetchDefaultList]);

  // Handle Search submit: encrypts identifier into the URL
  const handleClickSearchEmail = (userEmail) => {
    const trimmedInput = userEmail?.trim();
    if (!trimmedInput) {
      showToastMessage("Error", "Please enter a valid email to search.", "error", toast);
      return;
    }

    navigate(
      `?userIdentifier=${encryptURL(trimmedInput)}&page=1&sortField=${encryptURL(
        sortingField
      )}&sortOrder=${encryptURL(sortingOrder)}`
    );
  };

  // Reset: removes userIdentifier param completely
  const handleClickRefresh = () => {
    navigate(
      `?page=1&sortField=${encryptURL(sortingField)}&sortOrder=${encryptURL(
        sortingOrder
      )}&filter=${encryptURL("all")}`
    );
  };

  const handleChangePage = (_, value) => {
    const identifierParam = userIdentifierFromURL
      ? `&userIdentifier=${encryptURL(userIdentifierFromURL)}`
      : "";

    navigate(
      `?page=${value}&sortField=${encryptURL(sortingField)}&sortOrder=${encryptURL(
        sortingOrder
      )}&filter=${encryptURL(filter)}${identifierParam}`
    );
  };

  return (
    <div className="flex">
      <aside className="sticky top-0 h-screen basis-1/5">
        <Sidebar />
      </aside>

      <main className="basis-4/5">
        <Navbar
          handleClickSearchEmail={handleClickSearchEmail}
          handleClickRefresh={handleClickRefresh}
        />
        <div className="relative mb-6 overflow-x-auto rounded-lg shadow-md">
          <table className="w-full text-left text-sm text-gray-500">
            <thead className="bg-gray-700 text-xs uppercase text-white">
              <tr>
                <th className="px-6 py-3">User Identifier</th>
                <th className="px-6 py-3">User Feedback</th>
                <th className="px-6 py-3">REDCap Username</th>
                <th className="px-6 py-3">REDCap ID</th>
                <th className="px-6 py-3">CAR User</th>
                <th className="px-6 py-3">Request Date</th>
                <th className="px-6 py-3">Deletion Schedule Date</th>
              </tr>
            </thead>

            <tbody>
              {loader ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : carUsersList.length > 0 ? (
                carUsersList.map((user, index) => (
                  <tr
                    key={user?.id || user?.userIdentifier || index}
                    className="border-b odd:bg-white even:bg-gray-50 hover:bg-gray-100"
                  >
                    <td className="px-6 py-3 font-semibold text-gray-900">
                      {user?.userIdentifier ?? "NA"}
                    </td>
                    <td className="px-6 py-3 font-semibold text-gray-900">
                      {user?.feedbackForAccountDeletion ?? "NA"}
                    </td>
                    <td className="px-6 py-3">{user?.redUserName ?? "NA"}</td>
                    <td className="px-6 py-3">{user?.redCapId ?? "NA"}</td>
                    <td className="px-6 py-3">
                      <span
                        className={`rounded-full px-3 py-1 text-sm font-medium text-white ${
                          user?.isCarUser ? "bg-green-500" : "bg-red-500"
                        }`}
                      >
                        {user?.isCarUser ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      {user?.deletionRequestedAt
                        ? new Date(user.deletionRequestedAt).toLocaleDateString()
                        : "NA"}
                    </td>
                    <td className="px-6 py-3">
                      {user?.deletionScheduleDate
                        ? new Date(user.deletionScheduleDate).toLocaleDateString()
                        : "NA"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-gray-500">
                    No account deletion requests found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!loader && pageCount > 0 && (
          <div className="mb-10 flex h-20 items-center justify-center">
            <Pagination
              page={pageNumber}
              count={pageCount}
              color="primary"
              onChange={handleChangePage}
            />
          </div>
        )}
      </main>
    </div>
  );
};

export default AccountDeletion;